import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PagedList } from '@/components/PagedList'
import { TaskCard } from '@/components/TaskCard'
import { Button, Card, CardBody, CardHead, Empty, Stat } from '@/components/ui'
import { useJobCenter } from '@/context/JobCenterContext'
import { useLibraryStats } from '@/hooks/useLibraryStats'

type Filter = 'all' | 'active' | 'succeeded' | 'failed' | 'hidden'

const FILTERS: Filter[] = ['all', 'active', 'succeeded', 'failed', 'hidden']

/** 地址栏里的 `?filter=` → 合法筛选值；认不出来一律落「全部」，不猜 */
const readFilter = (raw: string | null): Filter =>
  (FILTERS as string[]).includes(raw ?? '') ? (raw as Filter) : 'all'

export function Jobs() {
  const {
    jobs,
    cancel,
    retry,
    dismiss,
    clearFinished,
    historyLoaded,
    historyError,
    refreshHistory,
    hiddenJobs,
    hiddenCount,
    restoreJobs,
  } = useJobCenter()
  // 作品数走接口（库里那份）—— 不再数浏览器镜像，清缓存也不会归零
  const library = useLibraryStats()
  /**
   * 筛选状态。
   *
   * 初值从地址栏读：生成页右栏历史卡底下那句「库里另有 N 条已清理的记录 →
   * 恢复」（2026-10-09 前叫「去恢复」）指的就是 `/jobs?filter=hidden`，落进来必须**直接停在「已清理」**上
   * —— 否则用户点完链接还要自己再找一次 chip，那颗链接等于没做。
   * 反过来，清空时提示里的「撤销」是就地完成的，不依赖这里。
   *
   * 点 chip 时写回地址栏（`replace`，不留历史），这样刷新 / 前进后退
   * 落在同一档筛选上，而不是「界面在已清理、地址栏说全部」。
   */
  const [params, setParams] = useSearchParams()
  const [filter, setFilterState] = useState<Filter>(() => readFilter(params.get('filter')))
  const setFilter = useCallback(
    (f: Filter) => {
      setFilterState(f)
      // 只写非默认值：`/jobs` 本身就是「全部」，地址栏不必拖一条 `?filter=all`
      setParams(f === 'all' ? {} : { filter: f }, { replace: true })
    },
    [setParams],
  )
  const [refreshing, setRefreshing] = useState(false)

  const list = useMemo(() => {
    switch (filter) {
      case 'active':
        return jobs.filter((j) => j.state === 'queued' || j.state === 'running')
      case 'succeeded':
        return jobs.filter((j) => j.state === 'succeeded' || j.state === 'partial')
      case 'failed':
        return jobs.filter((j) => j.state === 'failed')
      // 「已清理」不是主列表的一个子集 —— 它来自库里另一份查询（`hidden=only`），
      // 所以这里直接返回 `hiddenJobs`，不要在 `jobs` 上过滤
      // （主列表里压根不含已清理的记录，过滤出来永远是空）
      case 'hidden':
        return hiddenJobs
      default:
        return jobs
    }
  }, [jobs, filter, hiddenJobs])

  const counts = useMemo(
    () => ({
      all: jobs.length,
      active: jobs.filter((j) => j.state === 'queued' || j.state === 'running').length,
      succeeded: jobs.filter((j) => j.state === 'succeeded' || j.state === 'partial').length,
      failed: jobs.filter((j) => j.state === 'failed').length,
      // 用**后端下发的总数**，不是 `hiddenJobs.length`：后者是这一页，
      // 记录多到超过 limit 时数字会变小，而它是个「点进去恢复」的入口
      hidden: hiddenCount,
    }),
    [jobs, hiddenCount],
  )

  const totalCost = jobs.reduce((s, j) => s + (j.cost ?? 0), 0)
  const remoteCount = jobs.filter((j) => j.remote).length

  const doRefresh = async () => {
    setRefreshing(true)
    await refreshHistory()
    setRefreshing(false)
  }

  return (
    <div className="page page--wide">
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <Stat label="任务总数" value={counts.all} icon="activity" />
        <Stat label="进行中" value={counts.active} icon="bolt" />
        <Stat label="已完成" value={counts.succeeded} deltaTone="up" icon="check" />
        <Stat label="失败" value={counts.failed} deltaTone="down" icon="alert" />
        <Stat label="累计消耗" value={`¥${totalCost.toFixed(2)}`} icon="coin" />
        <Stat label="产出作品" value={library ? library.works : '—'} icon="folder" />
      </div>

      <Card>
        <CardHead
          title="任务队列"
          hint="提交后可以马上切到别的页面，生成不中断 · 点任意一条展开看结果、失败原因与提示词"
          icon="activity"
          right={
            <>
              <div className="row" style={{ gap: 6 }}>
                {(
                  [
                    ['all', '全部'],
                    ['active', '进行中'],
                    ['succeeded', '已完成'],
                    ['failed', '失败'],
                    // 入口常驻（哪怕 0 条）：否则用户清空之后**再也找不到恢复的地方**，
                    // 只会记得「清空了就没了」
                    ['hidden', '已清理'],
                  ] as [Filter, string][]
                ).map(([k, label]) => (
                  <button key={k} className="chip" data-active={filter === k} type="button" onClick={() => setFilter(k)}>
                    {label} {counts[k]}
                  </button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon="refresh"
                loading={refreshing}
                title="从后端任务库重新读取记录（失败原因以那里为准，不依赖浏览器缓存）"
                onClick={() => void doRefresh()}
              >
                刷新记录
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFinished}
                disabled={!counts.succeeded && !counts.failed}
              >
                清空已结束
              </Button>
            </>
          }
        />
        <CardBody tight>
          {!list.length ? (
            <Empty
              icon={filter === 'hidden' ? 'trash' : 'activity'}
              title={
                historyError
                  ? '后端任务库读取失败'
                  : !historyLoaded
                    ? '正在读取任务记录…'
                    : filter === 'hidden'
                      ? '没有已清理的记录'
                      : '这里还没有任务'
              }
              text={
                historyError
                  ? `${historyError} —— 任务记录都在库里，没丢；点右上角「刷新记录」重试。`
                  : !historyLoaded
                    ? '正在从后端任务库读取历史记录。'
                    : filter === 'hidden'
                      ? '在任意生成页的任务卡上点「移除记录」，记录会移到这里 —— 只影响列表，不删数据，随时可以恢复。'
                      : '去左侧任意工作台点一次「开始生成」。任务提交后立刻进入队列，可以继续操作其他页面。'
              }
            />
          ) : (
            <PagedList
              items={list}
              pageSize={10}
              pageSizes={[10, 20, 50]}
              resetKey={filter}
              unit="条"
              render={(j) => (
                /* `drawerOnClick`：和右栏「本页历史」同一个交互 —— 整条点击右侧展开。
                   任务中心本身就是**历史列表**，就地展开会在一列里往下撑，看结果还得滚。
                   「已清理」视图多传一个 `restore`：那颗按钮从「移除」换成「恢复」。 */
                <TaskCard
                  key={j.id}
                  job={j}
                  onCancel={cancel}
                  onRetry={retry}
                  onDismiss={dismiss}
                  drawerOnClick
                  restore={filter === 'hidden' ? (id) => void restoreJobs([id]) : undefined}
                />
              )}
            />
          )}
        </CardBody>
      </Card>

      {jobs.some((j) => j.state === 'failed' && j.error?.code === 'NETWORK') ? (
        <div className="section-gap">
          <Card>
            <CardBody>
              <div className="text-sm">
                出现 <span className="mono">NETWORK</span> 错误说明后端不可达。当前数据层开关为{' '}
                <span className="mono">VITE_API_MODE</span>，若已切到 <span className="mono">http</span>，
                请确认 Python 服务已在 <span className="mono">127.0.0.1:8000</span> 启动。
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      <div className="section-gap text-xs muted">
        <b>任务记录存在数据库里</b>（<span className="mono">GET /api/aigc/jobs</span>，本次读出{' '}
        {remoteCount} 条历史）。本地只缓存本机这一次会话提交的最近 60 条
        （<span className="mono">hw_job_history_v1</span>），那是后端挂掉时的降级缓存，
        <b>不是真源</b>。<br />
        「移除记录」（以及本页右上角的「清空已结束」）也<b>不是删除</b>：只是把记录标成已清理
        （库里的行原样留着），所以在上面那个「已清理」里可以随时恢复 —— 清缓存、换浏览器
        都<b>不会</b>让记录消失。刚清完的几秒里，右下角还会给一颗<b>「撤销」</b>，不必先去翻列表。
        <br />
        生成页结果卡上那颗<b>「清空」是另一回事</b>：它只收起右栏的产出预览，<b>记录一条都不动</b>
        —— 想清掉记录请用任务卡上的「移除记录」。
        点开任意任务还能看到任务号、模型、规格、消耗与本次提交的提示词；产出结果同时归档到「我的作品」。
      </div>
    </div>
  )
}
